import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cal, { getCalApi } from '@calcom/embed-react';
import { useLanguage } from '../context/LanguageContext';
import { getWhatsAppUrl } from '../constants/contact';
import { getPackageById } from '../data/relocation';

// Configure your Cal.com booking link here
const CAL_LINK = "mundalo";

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const sessionId = searchParams.get('session_id');
  const packageId = searchParams.get('package');
  
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(!!sessionId);
  
  const packageInfo = packageId ? getPackageById(packageId) : null;

  // Verify payment status if session_id is present
  useEffect(() => {
    if (!sessionId) return;

    const verifyPayment = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/relocation/checkout/status/${sessionId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setPaymentStatus(data.payment_status);
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  const isPaid = paymentStatus === 'paid';
  const isRelocationPurchase = !!packageId;

  // Initialize Cal.com embed
  useEffect(() => {
    (async function () {
      const cal = await getCalApi();
      cal("ui", {
        theme: "dark",
        styles: { branding: { brandColor: "#2CA8A0" } },
        hideEventTypeDetails: false
      });
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F9F6EF] border-b border-[#F4ECD8]">
        <div className="container-wide mx-auto px-8 py-5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-white.png" alt="Mundalo" className="block h-7 w-[140px] object-contain object-left" />
            </a>
            <nav className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm font-medium text-[#1A5C6E] hover:text-[#2CA8A0] transition">{t('nav.home')}</a>
              <a href="/services" className="text-sm font-medium text-[#1A5C6E] hover:text-[#2CA8A0] transition">{t('nav.services')}</a>
              <a href="/relocation/cdmx" className="text-sm font-medium text-[#1A5C6E] hover:text-[#2CA8A0] transition">{t('nav.relocationHub')}</a>
              <a href="/contact" className="text-sm font-medium text-[#1A5C6E] hover:text-[#2CA8A0] transition">{t('nav.contact')}</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="section">
        <div className="container-narrow mx-auto text-center">
          <div className="max-w-2xl mx-auto">
            {loading ? (
              <div className="py-12">
                <div className="w-12 h-12 border-4 border-[#E0D6B8] border-t-[#2CA8A0] rounded-full animate-spin mx-auto mb-4" />
                <p className="body-md text-[#1A5C6E]">{t('thankYou.confirmingPayment')}</p>
              </div>
            ) : (
              <>
                {/* Success Icon */}
                <div className="w-16 h-16 rounded-full bg-[#2CA8A0] flex items-center justify-center mx-auto mb-8">
                  <svg className="w-8 h-8 text-[#F9F6EF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                {isRelocationPurchase && isPaid ? (
                  <>
                    <h1 className="heading-xl mb-4" data-testid="thank-you-heading">
                      {t('thankYou.paymentConfirmed')}
                    </h1>
                    {packageInfo && (
                      <p className="body-lg mb-4 text-[#2CA8A0]">
                        {packageInfo.name} - {packageInfo.price}
                      </p>
                    )}
                    <p className="body-lg mb-12 text-[#1A5C6E]">
                      Your relocation plan is now active. We'll be in touch within 24 hours to schedule your onboarding.
                    </p>
                  </>
                ) : isRelocationPurchase ? (
                  <>
                    <h1 className="heading-xl mb-4" data-testid="thank-you-heading">
                      {t('thankYou.title')}
                    </h1>
                    <p className="body-lg mb-12 text-[#1A5C6E]">
                      {t('thankYou.processing')}
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className="heading-xl mb-6" data-testid="thank-you-heading">
                      {t('thankYou.nextStep')}
                    </h1>
                    <p className="body-lg mb-12 text-[#1A5C6E]">
                      {t('thankYou.received')}
                    </p>
                  </>
                )}

                {/* Calendar Embed */}
                <div className="bg-[#FFFFFF] border border-[#E0D6B8] p-8 mb-8" data-testid="calendar-section">
                  <p className="label mb-4">{t('thankYou.scheduleCall')}</p>
                  <p className="body-md mb-6 text-[#1A5C6E]">
                    {isRelocationPurchase && isPaid 
                      ? t('thankYou.bookOnboarding')
                      : t('thankYou.bookSooner')
                    }
                  </p>
                  <div className="bg-[#FFFFFF] overflow-hidden rounded" style={{ minHeight: '400px' }}>
                    {CAL_LINK ? (
                      <Cal
                        calLink={CAL_LINK}
                        style={{ width: "100%", height: "100%", minHeight: "400px" }}
                        config={{
                          theme: "dark",
                          layout: "month_view"
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8">
                        <svg className="w-16 h-16 text-[#E0D6B8] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-[#5A6B73] text-center mb-4">Calendar scheduling coming soon</p>
                        <p className="text-[#4a4a4a] text-sm text-center">
                          Contact us via WhatsApp below to schedule your call
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* WhatsApp Direct Link */}
                <a
                  href={getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 font-semibold hover:bg-[#20bd5a] transition-colors"
                  data-testid="whatsapp-link"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  {t('thankYou.messageWhatsApp')}
                </a>

                <div className="mt-12">
                  <a href="/" className="btn-secondary">{t('thankYou.returnHome')}</a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="section-sm border-t border-[#F4ECD8]">
        <div className="container-wide mx-auto text-center">
          <img src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-white.png" alt="Mundalo" className="block h-7 w-[140px] object-contain object-left" />
          <p className="body-sm text-[#5A6B73]">© {new Date().getFullYear()} Mundalo. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ThankYou;
